import request from '../utils/request';

/**
 * 通用合同签署（法大大【老版 v2】接口）—— 后台管理接口封装
 *
 * 一套接口签所有合同：换合同只换「PDF 内容 + 表单内容」，
 * 模板在 sign_contract_template 表登记（也可在「合同模板管理」页面维护），本文件不随合同类型变化。
 *
 * 后端控制器：com.web.admin.controller.SignContractController（路由 /sys/SignContract）
 * 老版网关：https://textapi.fadada.com/api2/  SDK：com.fadada.sdk:fdd:0.0.1
 * ⚠️ 我方法大大账号是老版本（买的是老版额度），禁止改回 v5 接口。
 *
 * 接口清单（12 个业务 + 5 个模板维护/体检）：
 *   templateList / stat / send / retry / restart / resend / signUrl / list / detail / refresh
 *   / downloadUrl / urge / cancel
 *   templateAll / templateSave / templateDelete / pdfFields / precheck
 */

// ==================== 模板（发起签署用） ====================

/**
 * 合同模板列表（发起签署下拉用，含表单字段定义 fieldSchema）
 * 返回项带 ready / unreadyReason：未配置 PDF 来源或印章的模板不可发起，前端应禁用并展示原因。
 * 同时返回「实际生效」的 corpSignatureId / signKeywordA / signKeywordB（模板为空时已回退到 yml）。
 */
export function templateList() {
    return request({
        url: '/sys/SignContract/templateList',
        method: 'post'
    })
}

// ==================== 模板管理（合同模板管理页面用） ====================

/**
 * 合同模板维护列表（含停用）
 * 返回「库里存的原值」+「实际生效值」（effectiveCorpSignatureId / effectiveSignKeywordA / effectiveSignKeywordB）
 */
export function templateAll() {
    return request({
        url: '/sys/SignContract/templateAll',
        method: 'post'
    })
}

/**
 * 新增 / 保存合同模板（有 id 为更新，无 id 为新增）
 * @param {Object} data SignContractTemplate
 *   templateCode 模板编码(必填,唯一) / templateName 模板名称(必填)
 *   bizType 业务分类 / pdfUrl PDF地址(必填) / pdfName PDF文件名
 *   partyATitle 甲方称谓 / partyBTitle 乙方称谓 / partyBName 我方公司名
 *   partyBAutoSign 乙方自动盖章 1/0
 *   corpSignatureId 本合同专用印章ID(空=用 yml) / signKeywordA / signKeywordB 落章关键字(空=用 yml)
 *   fieldSchema 表单字段定义 JSON 字符串 / signRule 签署顺序 / state 1启用 0停用 / remark 备注
 */
export function templateSave(data) {
    return request({
        url: '/sys/SignContract/templateSave',
        method: 'post',
        data
    })
}

/**
 * 删除合同模板（逻辑删除，历史合同不受影响）
 */
export function templateDelete(id) {
    return request({
        url: '/sys/SignContract/templateDelete',
        method: 'post',
        params: {id}
    })
}

/**
 * 识别 PDF 表单域，并返回可直接使用的 field_schema 建议
 * @param {String} pdfUrl PDF 的 http(s) 地址或服务器本地绝对路径
 * @param {String} [templateCode] 传了则按「模板 > yml」解析落章关键字来数字数
 * @param {String} [keywordA] 甲方落章关键字（覆盖模板解析）
 * @param {String} [keywordB] 乙方落章关键字（覆盖模板解析）
 * @returns data: {pages, fields:[{name,type,typeName,page,fillable}], suggestedSchema:[...], keywords:{关键字:次数}}
 */
export function detectPdfFields(pdfUrl, templateCode, keywordA, keywordB) {
    return request({
        url: '/sys/SignContract/pdfFields',
        method: 'post',
        params: {pdfUrl, templateCode, keywordA, keywordB}
    })
}

// ==================== 发起前预检 ====================

/**
 * 发起前预检：模板就绪 / PDF 可达 / 字段对齐 / 落章关键字命中 / 印章 / 必填项 / 手机号
 * @param {Object} data 与 sendContract 同一份入参（SignContractSendDTO）
 * @returns data: {ok, items:[{name,level,detail}], errorCount, warnCount, msg}
 */
export function precheckContract(data) {
    return request({
        url: '/sys/SignContract/precheck',
        method: 'post',
        data
    })
}

// ==================== 甲方账号 / 实名预检（2026-09-11 新增） ====================

/**
 * 甲方手机号账号/实名预检（只读）：
 * 查该手机号在法大大是否已有客户号（同号同账号，直接复用）、企业号是否已授权。
 * 目的：填完手机号就知道发起后会发生什么，避免"发起完才发现要实名"。
 * @param {Object} data SignContractSendDTO（至少含 partyAPhone / partyAType）
 * @returns data: {hasAccount, customerId, realNameKnown, realNameNote, msg}
 */
export function partyAAccountCheck(data) {
    return request({
        url: '/sys/SignContract/partyAAccountCheck',
        method: 'post',
        data
    })
}

/**
 * 乙方电子印章列表（法大大平台可用印章，已剥掉 base64 图片）
 * @param {String} [customerId] 客户号，留空取乙方企业号
 * @param {String} [templateCode] 传了则一并返回该模板当前生效的印章ID
 * @returns data: {signatures:[{signatureId, signatureType, signatureSource}], usingSignatureId, usingSignatureFound}
 */
export function signatureList(customerId, templateCode) {
    return request({
        url: '/sys/SignContract/signatureList',
        method: 'post',
        params: { customerId, templateCode }
    })
}

// ==================== 签署任务 ====================

/**
 * 签署总览统计（列表页顶部卡片）
 * @returns {pending, done, failed, canceled, rejected, total}
 */
export function contractStat() {
    return request({
        url: '/sys/SignContract/stat',
        method: 'post'
    })
}

/**
 * 发起合同签署
 * @param {Object} data SignContractSendDTO
 *   templateCode 模板编码(必填) / subject 标题
 *   partyAType 甲方类型 corp|person / partyAName 甲方名称
 *   partyARep 授权代表人 / partyAPhone 电话 / partyAAddress 地址
 *   partyACustomerId 甲方在法大大的客户编号(选填，填了跳过自动注册)
 *   partyBName 乙方名称(不填取模板默认) / redirectUrl 回跳地址
 *   formData 表单内容 {PDF表单域名:值} / bizType 业务分类 / bizId 业务ID / remark 备注
 * @returns 成功时 data 内含 contractNo / signTaskId / signUrl / previewUrl
 */
export function sendContract(data) {
    return request({
        url: '/sys/SignContract/send',
        method: 'post',
        data
    })
}

/**
 * 发起失败后重新发起（复用原合同编号与已填内容，不重填）
 * 仅适用于「还没成功发起」的合同（发起失败态）。
 * @param {String} contractNo 合同编号
 */
export function retryContract(contractNo) {
    return request({
        url: '/sys/SignContract/retry',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 重新发起（**新建一份合同**，复用原甲方信息与表单内容）
 * 用于「已拒签 / 已撤销 / 已过期 / 发起失败」——老版已上传的合同内容不可改，只能换新合同重签。
 * @param {String} contractNo 原合同编号
 * @returns 成功时 data 内含新合同号 contractNo / signUrl
 */
export function restartContract(contractNo) {
    return request({
        url: '/sys/SignContract/restart',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 重发 / 催签：再次通知甲方并重取最新签署链接（可选补充甲方手机号）
 * @param {String} contractNo 合同编号
 * @param {String} [phone] 补充/更正甲方手机号
 * @returns 成功时 data 内含 signUrl / urged / partyAPhone
 */
export function resendContract(contractNo, phone) {
    return request({
        url: '/sys/SignContract/resend',
        method: 'post',
        params: {contractNo, phone}
    })
}

/**
 * 重新获取甲方签署链接（链接失效或需重发时用）
 */
export function getSignUrl(contractNo) {
    return request({
        url: '/sys/SignContract/signUrl',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 强制【重新生成】甲方签署链接（旧链接失效 / 提示"交易号重复" / 需要作废旧链接时用）。
 * ⚠️ 会用全新交易号重新取链接，【旧链接立即作废】，请把新链接重新发给甲方。
 * 返回：{ contractNo, signUrl(对外主链接), linkType(verify|sign), verifyUrl, signShortUrl, msg }
 * 【2026-09-12】后端已按"三态"决策：未实名时 signUrl 是【实名短链】，已实名时是【签署短链】，
 * 前端展示 signUrl 即可，不要再自行拼签署链接转发。
 */
export function regenSignUrl(contractNo) {
    return request({
        url: '/sys/SignContract/regenSignUrl',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 获取甲方实名认证链接。
 * 甲方点开签署链接提示「对不起，您尚未实名认证」时，把这条实名链接转发给他；
 * 完成实名后【原签署链接即可正常签署】，无需重新发起合同。
 * @param {String} contractNo 合同编号
 */
export function getVerifyUrl(contractNo) {
    return request({
        url: '/sys/SignContract/verifyUrl',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 签署列表（分页）
 * @param {Object} params {templateCode, state, partyAName, startTime, endTime, pageNo, pageSize}
 */
export function contractList(params) {
    return request({
        url: '/sys/SignContract/list',
        method: 'post',
        params
    })
}

/**
 * 合同详情（含签署记录时间轴）
 * @param {String} contractNo 合同编号
 * @param {Boolean} refreshFirst 是否先向法大大刷新一次最新状态，默认 true
 */
export function contractDetail(contractNo, refreshFirst = true) {
    return request({
        url: '/sys/SignContract/detail',
        method: 'post',
        params: {contractNo, refreshFirst}
    })
}

/**
 * 刷新合同签署状态（也可由法大大回调自动刷新）
 */
export function refreshContract(contractNo) {
    return request({
        url: '/sys/SignContract/refresh',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 获取已签署完成的 PDF 下载地址（内部会先刷新状态；取到后落库）
 */
export function getDownloadUrl(contractNo) {
    return request({
        url: '/sys/SignContract/downloadUrl',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 催签（让法大大再次通知未签署的参与方）
 */
export function urgeContract(contractNo) {
    return request({
        url: '/sys/SignContract/urge',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 撤销合同（作废该签署任务）
 * ⚠️ 老版法大大没有在线撤销接口，这里只做本地状态标记，实际作废需在法大大后台处理。
 */
export function cancelContract(contractNo, note) {
    return request({
        url: '/sys/SignContract/cancel',
        method: 'post',
        params: {contractNo, note}
    })
}

// ==================== 证据链增强 / 纠错（2026-09-12 补接线） ====================

/**
 * 取甲方【签署意愿认证】链接（人脸 / 短信意愿确认）。
 * 用途：签完之后补强证据链——证明「确实是本人自愿签的」。
 * 属【可选增强】，不签也不影响合同效力，因此放在「更多」里，不进主流程。
 * @param {String} contractNo 合同编号
 * @returns data: {code:"1", msg:"success", url, tip}（url 即可转发的意愿认证链接；tip 为法大大侧的提示）
 */
export function beforeAuthSignUrl(contractNo) {
    return request({
        url: '/sys/SignContract/beforeAuthSignUrl',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 合同验签：校验已签 PDF 上的签章真伪（法大大侧比对）。
 * 用途：归档后举证 / 客户质疑合同真实性时自证。
 * @param {String} contractNo 合同编号
 * @returns data: {code, msg, ...} 法大大 contract_verify 原始返回
 */
export function verifyContract(contractNo) {
    return request({
        url: '/sys/SignContract/verify',
        method: 'post',
        params: {contractNo}
    })
}

/**
 * 按法大大【权威查询】强制重算状态。
 * 用途：本地状态明显不对（例如显示"已完成"但 PDF 上没有甲方签名）时，用它把甲乙双方
 * 逐方查一遍并落库纠正，返回双方明细便于核对。比「刷新」更彻底（刷新会受终态保护影响）。
 * @param {String} contractNo 合同编号
 * @returns data: {code:"1", msg:"success", state, stateName, partyASigned, partyBSigned,
 *                 diag:{contractNo, partyACustomerId, corpCustomerId, localStateBefore, localStateNameBefore,
 *                       localPartyASignStatus, localPartyBSignStatus,
 *                       partyA:{signed, rejected, notRealNamed, authoritative, realNameChecked, authStatus, signStatusDesc, desc},
 *                       partyB:{同上}}}
 *   注意：甲乙双方的查询明细在 diag 下，不在 data 顶层。
 */
export function recheckContract(contractNo) {
    return request({
        url: '/sys/SignContract/recheck',
        method: 'post',
        params: {contractNo}
    })
}

// ==================== 通用文件上传（MinIO） ====================

/**
 * 通用文件上传到 MinIO（合同 PDF / 甲方证件 / 银行卡图片等），返回可访问 URL 字符串。
 * 后端接口：com.web.admin.controller.PictureController#uploadFile（路由 /sys/picture/uploadFile）
 * @param {File} file 文件对象（el-upload 回调里的 file 或 raw）
 * @param {String} [folder] MinIO 目录，默认 contract；模板 PDF 用 contractTemplate，签署附件用 signAttach
 * @returns 成功时 res.data 为 URL 字符串
 */
export function uploadToMinio(file, folder = 'contract') {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', folder);
    return request({
        url: '/sys/picture/uploadFile',
        method: 'post',
        data: fd
    });
}

